'use client'

import { useId, useState } from 'react'

import styles from './PerformancePromoBlock.module.scss'

interface PromotionDetailsProps {
  /** Descripción de la promoción, ya deduplicada del título. */
  readonly description: string
  /**
   * Título de la promoción, solo para el nombre accesible del botón. La rejilla
   * pinta hasta cuatro «Ver más» idénticos: sin él, el rotor de un lector de
   * pantalla lista cuatro botones indistinguibles.
   */
  readonly title?: string
}

/**
 * «Ver más» de una tarjeta de la rejilla: revela la descripción de esa
 * promoción sin salir de la ficha.
 *
 * El panel va **después** del botón, no antes como en la maqueta: plegado se ve
 * exactamente igual (el panel no ocupa nada), y desplegado evita que el propio
 * botón que acabas de pulsar salte hacia abajo empujado por el texto.
 *
 * Mismo criterio que `PromotionsDisclosure` con el estado plegado: el panel
 * permanece montado y se oculta con el atributo nativo `hidden`, así
 * `aria-controls` nunca apunta a un id ausente del DOM.
 */
export function PromotionDetails({ description, title }: PromotionDetailsProps) {
  const [expanded, setExpanded] = useState(false)
  const panelId = useId()

  const action = expanded ? 'Ver menos' : 'Ver más'

  return (
    <>
      <button
        type="button"
        className={styles.cardMore}
        // El texto visible es prefijo del nombre accesible (WCAG 2.5.3, «Label
        // in Name»): no se sustituye «Ver más», se le añade contexto.
        aria-label={title ? `${action} sobre ${title}` : undefined}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => setExpanded((value) => !value)}
      >
        {action}
      </button>

      <p id={panelId} className={styles.promoText} hidden={!expanded}>
        {description}
      </p>
    </>
  )
}
